import React, { useState } from "react";
import styled from "styled-components";
import { FiArrowUpRight, FiBookOpen, FiBriefcase, FiLayers } from "react-icons/fi";
import Timepoint from "./Timepoint";
import { timelineEntries } from "./timelineEntries";

const filters = [
  { label: "All", Icon: FiLayers },
  { label: "Experience", Icon: FiBriefcase },
  { label: "Education", Icon: FiBookOpen },
];

const Timeline = () => {
  const [filter, setFilter] = useState("All");
  const visible = timelineEntries.filter((entry) => filter === "All" || entry.category === filter);

  return (
    <Container id="timeline" aria-labelledby="timeline-heading">
      <Layout>
        <Introduction>
          <Eyebrow>Experience & education</Eyebrow>
          <h2 id="timeline-heading">The journey<br /><span>behind the work.</span></h2>
          <Description>From my first lines of code to building full-stack applications. The roles, studies, and experiences that have shaped how I work today.</Description>
          <Legend><span aria-hidden="true" /> Current roles first, then earlier chapters.</Legend>
          <Filters role="group" aria-label="Filter timeline">
            {filters.map(({ label, Icon }) => (
              <button key={label} type="button" aria-pressed={filter === label} onClick={() => setFilter(label)}><Icon aria-hidden="true" />{label}</button>
            ))}
          </Filters>
          <Count role="status">{visible.length} {filter === "Education" ? "education milestones" : filter === "Experience" ? "roles" : "chapters"} · {filter === "All" ? "Experience and education" : filter}</Count>
          <CertificateLink href="#certificates">Explore my certificates <FiArrowUpRight aria-hidden="true" /></CertificateLink>
        </Introduction>
        <Entries aria-label="Career and education timeline">
          {visible.map((entry, index) => (
            <Timepoint key={entry.id} timepoint={entry} last={index === visible.length - 1} />
          ))}
        </Entries>
      </Layout>
    </Container>
  );
};

export default Timeline;

const Container = styled.section`
  padding: 3.5rem 1rem;
  color: #253d31;
  background: radial-gradient(ellipse at 0% 0%, #edf6ef, transparent 45%), #fcfdfc;
  @media (min-width: 600px) { padding: 5rem 5%; }
  @media (min-width: 1024px) { padding-block: 6rem; }
`;
const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2rem;
  max-width: 1120px;
  margin-inline: auto;
  @media (min-width: 960px) { grid-template-columns: minmax(0, 0.75fr) minmax(0, 1.5fr); align-items: start; gap: 3rem; }
`;
const Introduction = styled.div`
  min-width: 0;
  h2 { font-size: clamp(2rem, 3.5vw, 3rem); font-weight: 500; line-height: 1.15; letter-spacing: -0.055em; }
  h2 span { color: #008369; }
  @media (min-width: 960px) and (min-height: 720px) { position: sticky; top: 7.5rem; }
`;
const Eyebrow = styled.p`margin-bottom: 1.25rem; color: #476259; font-size: 0.65rem; font-weight: 600; letter-spacing: 0.13em; text-transform: uppercase;`;
const Description = styled.p`max-width: 44ch; margin-top: 1.25rem; color: #596b60; font-size: 0.85rem; line-height: 1.85;`;
const Legend = styled.p`
  display: flex; align-items: center; gap: 0.5rem; margin-top: 1.5rem; color: #65736b; font-size: 0.65rem; line-height: 1.7;
  span { width: 6px; height: 6px; flex-shrink: 0; border-radius: 50%; background: #008369; }
`;
const Filters = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 1.5rem;
  button {
    display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem;
    min-height: 44px; padding: 0.6rem 0.8rem; border: 1px solid #d5e3d9; border-radius: 999px;
    background: #fff; color: #47594f; font-size: 0.7rem; cursor: pointer;
    &[aria-pressed="true"] { background: #183c32; border-color: #183c32; color: #fff; }
    &:hover:not([aria-pressed="true"]) { background: #eaf3ed; }
    &:focus-visible { outline: 2px solid #008369; outline-offset: 3px; }
  }
  @media (max-width: 359px) { gap: 0.3rem; button { padding-inline: 0.6rem; font-size: 0.65rem; } }
`;
const Count = styled.p`margin-top: 0.85rem; color: #65736b; font-size: 0.65rem; line-height: 1.7;`;
const CertificateLink = styled.a`
  display: inline-flex; align-items: center; gap: 0.6rem; min-height: 44px; margin-top: 1rem;
  color: #007b61; font-size: 0.75rem; font-weight: 500; text-decoration: none;
  &:hover { text-decoration: underline; text-underline-offset: 4px; }
  &:focus-visible { outline: 2px solid #008369; outline-offset: 3px; }
`;
const Entries = styled.ol`list-style: none; min-width: 0;`;
